import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { StressResultsService } from '../../services/stress-results.service';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { SelectItem } from 'primeng/api';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Store } from '@ngneat/elf';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { SingleLoadResultsUi } from '../../models/perivis-results-state.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { BarlowBurstResultsModel } from '../../models/barlow-burst-results.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-single-load-burst',
  templateUrl: './single-load-burst.component.html',
  styles: [],
  standalone: false
})
export class SingleLoadBurstComponent implements OnInit, OnDestroy {
  private _pressureUnit: string;
  private selectedString: string;

  public burstResults: BarlowBurstResultsModel[];
  public loadCases: SelectItem[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public selectedMode: string = 'single';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public yAxisTitle = '';
  public xAxisTitle = '';
  public userUnits: UserUnitsModel;
  public plotName: string;
  public signalRChange: boolean;
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];
  public longLengthUnit: string;

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.burstResultsStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.burstResultsStore.state.selectedMode || 'single';
    this.selectedResultDisplay = this.burstResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.burstResultsStore.state.selectedDepthView || 'md';
  }
  get componentId(): string {
    return this._componentId;
  }
  public burstResultsStore: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = this.userUnits.longLengths;
    this._pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this.xAxisTitle = `Burst Pressure (${this._pressureUnit})`;
    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    // this.refreshResults(true, false, false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds.tubularId;
    this.refreshResults(true, false, false);

    this.columnDefs = [
      { header: 'Burst Load', field: 'burstLoad' },
      { header: 'Barlow Burst Rating', field: 'barlowBurstRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Burst Load (${this._pressureUnit})`, field: 'burstLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Burst Rating (${this._pressureUnit})`, field: 'barlowBurstRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true, false);
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode: boolean): Promise<void> {
    this.isLoading.set(isLoading);

    this.xAxisTitle = `Burst Pressure (${this.userUnits?.pressure})`;

    if ((!this.burstResults || calledFromSignalR) && !calledFromMode) {
      try {
        this.burstResults = await lastValueFrom(this._stressResultsService.getBarlowBurstResults());
        this.signalRChange = true;
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.burstResults && (this.burstResults.length === 0 || this.burstResults[0].loadCaseName === '')) {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    if (this.burstResultsStore.state.selectedMode == "single") {
      this.handleSingleBurstLoad(this.burstResults);
    } else {
      this.handleMultiBurstLoad(this.burstResults);
    }

    this.isLoading.set(false);
  }

  onModeChange(e): void {
    this.selectedMode = e.value;
    this.burstResultsStore.update(state => ({ ...state, selectedMode: e.value }));
    if (this.burstResultsStore.state.selectedMode == "multiple") {
      this.burstResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Burst Load', value: 'burstLoad' } }));
    } else {
      // this.xAxisTitle = `Burst Pressure (psi)`;
    }

    this.plotName = this.setPlotName();
    this.refreshResults(true, false, true);
  }

  onSelectedResultDisplayUpdated(e): void {
    this.selectedResultDisplay = e.value;
    this.burstResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  setLoadCase(e): void {
    if (this.burstResultsStore.state.selectedMode === "single") {
      this.burstResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.burstResults;
    } else {
      this.burstResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }
  }

  setPlotName(): string {
    return 'barlowBurstPlot - ' + this.selectedString + ' - ' + this.burstResultsStore.state.selectedMode + ' ' + this.burstResultsStore.state.selectedLoadCase.label;
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.burstResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleBurstLoad(burstResults: any[]) {
    this.columnDefs = [
      { header: 'Burst Load', field: 'burstLoad' },
      { header: 'Barlow Burst Rating', field: 'barlowBurstRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Burst Load (${this.userUnits?.pressure})`, field: 'burstLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Burst Rating (${this.userUnits?.pressure})`, field: 'barlowBurstRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];

    this.results = [];

    this.loadCases = burstResults?.map(lc => {
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.burstResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.burstResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.burstResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }
    this.plotName = this.setPlotName();
    this.results = this.burstResultsStore.state.selectedLoadCase?.value.burstResults || [];
  }

  private handleMultiBurstLoad(burstResults: any[]) {
    this.columnDefinitions.splice(2, this.columnDefinitions.length);
    this.columnDefs = [];
    this.results = [];

    this.loadCases = [
      { label: 'Burst Load', value: 'burstLoad' },
      { label: 'Barlow Burst Rating', value: 'barlowBurstRating' }
    ]

    this.xAxisTitle = this.burstResultsStore.state.selectedLoadCase.value === 'burstLoad' ? `Burst Load (${this._pressureUnit})` : `Barlow Burst Rating (${this._pressureUnit})`;

    burstResults.forEach(load => {
      const loadName: string = load.loadCaseName + ` (${this._pressureUnit})`;
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      });

      const backupArray = [];

      load.burstResults.forEach(loadResult => {
        const result = {
          [loadNameCamel]: loadResult[this.burstResultsStore.state.selectedLoadCase.value],
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (burstResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });
    });
    this.plotName = this.setPlotName();
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
